/**
 * JoinEditableOnPill — inline editor for a JOIN's ON predicate. Clicking the
 * pill opens one of two editors:
 *
 *  - structured: when the predicate is a single `‹col› = ‹col›` whose two sides
 *    resolve to the tables this ON can see (`listInScopeTables`: its own join
 *    tree up to this JOIN, per the engine's comma/JOIN precedence — never the
 *    schema's other tables), matched the way the engine binds names,
 *    two table+column dropdown pairs (each side a table <select> + column
 *    <select>) make the join key self-explanatory. Delimited references
 *    (`"order items".x`, `[Order Lines].[qty]`) qualify too; a re-pointed side
 *    is written dialect-exact. "Edit as text" drops to the free-text editor
 *    for anything richer.
 *  - free-text: the fallback for multi-condition / function / cast predicates
 *    (and the explicit "Edit as text" escape). Commits on Enter / blur.
 *
 * Both paths commit a whole-ON replacement via `modifyJoinOn` (the only path
 * SR-Edit exposes) and cancel on Escape.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN +
 * edit ON predicate.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import { modifyJoinOn } from '../../edit/edits/modifyJoinOn';
import { parseSimpleJoinOn, resolveOnSide } from './joinOnParse';
import { listInScopeTables } from './inScopeTables';
import { StructuredOnEditor } from './JoinOnStructuredEditor';

export interface JoinEditableOnPillProps {
  readonly testId: string;
  readonly text: string;
  readonly joinIndex: number;
  readonly editContext: VqeEditContext;
}

export function JoinEditableOnPill({ testId, text, joinIndex, editContext }: JoinEditableOnPillProps) {
  const [editing, setEditing] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const engine = editContext.service.engineId;
  // The tables this ON can name — the statement's own, read from its
  // structure (`scope.reachableTables` also carries every schema table) —
  // for the per-side table dropdowns of the structured editor. Built only
  // while the editor is open: a statement with hundreds of JOINs renders a
  // pill per ON, and none of them needs its list until it is clicked. `text`
  // is the re-read trigger: the structure changes with the statement.
  const tables = useMemo(() => {
    if (!editing) return [];
    try {
      const structure = extractStructureForContext(editContext);
      return listInScopeTables(structure, joinIndex, engine, editContext.service.getSchemaSnapshot());
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, editContext, joinIndex, engine, text]);

  // Resolve the simple-equality shape into each side's table + column. Null →
  // no structured editor (free-text only).
  const structured = useMemo(() => {
    if (!editing) return null;
    const parsed = parseSimpleJoinOn(text);
    if (!parsed) return null;
    const left = resolveOnSide(parsed.left, tables, engine);
    const right = resolveOnSide(parsed.right, tables, engine);
    if (!left || !right) return null;
    return { left: { ...left, text: parsed.left }, right: { ...right, text: parsed.right } };
  }, [editing, text, tables, engine]);

  useEffect(() => {
    if (!editing) setValue(text);
  }, [text, editing]);
  useEffect(() => {
    if (editing && (textMode || !structured) && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing, textMode, structured]);

  const commitText = (): void => {
    const trimmed = value.trim();
    setEditing(false);
    if (trimmed.length === 0 || trimmed === text.trim()) {
      setValue(text);
      return;
    }
    void modifyJoinOn(joinIndex, trimmed, toCommitCtx(editContext));
  };

  const commitStructured = (next: string): void => {
    setEditing(false);
    if (next.trim() !== text.trim()) void modifyJoinOn(joinIndex, next, toCommitCtx(editContext));
  };

  if (!editing) {
    return (
      // The pill OWNS its own truncation (`block max-w-full truncate`) instead
      // of being clipped by an ancestor: an ancestor `truncate` can't ellipsize
      // an atomic inline-level child, so a wide ON predicate used to be sliced
      // mid-glyph with its right border cut off. `shrink` lets the tooltip
      // wrapper (the actual flex item) shrink so `max-w-full` has a real cap,
      // and the full predicate stays readable in the tooltip.
      <VqeTooltip content={`${text} · click to edit ON predicate`} shrink>
        <button
          type="button"
          className="block max-w-full truncate rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px] transition-transform hover:underline active:scale-[0.97]"
          data-testid={testId}
          onClick={() => {
            setTextMode(false);
            setEditing(true);
          }}
        >
          {text}
        </button>
      </VqeTooltip>
    );
  }

  if (structured && !textMode) {
    return (
      <StructuredOnEditor
        testId={testId}
        tables={tables}
        structured={structured}
        engine={engine}
        onCancel={() => setEditing(false)}
        onEditAsText={() => {
          setValue(text);
          setTextMode(true);
        }}
        onApply={commitStructured}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commitText}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitText();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setEditing(false);
          setValue(text);
        }
      }}
      data-testid={`${testId}-input`}
      className="rounded border border-input bg-background px-1.5 py-0.5 font-mono text-[11px]"
      aria-label="Edit ON predicate"
    />
  );
}
